import React from 'react';
import ReactDOMServer from 'react-dom/server';

/**
 * Component Transformer - Converts JSON layout to HTML
 */
class ComponentTransformer {
  constructor() {
    this.componentMap = new Map();
    this.initializeComponents();
  }

  /**
   * Initialize component transformers
   */
  initializeComponents() {
    // Text Block Component
    this.componentMap.set('text-block', this.transformTextBlock);
    this.componentMap.set('text', this.transformTextBlock);
    this.componentMap.set('paragraph', this.transformTextBlock);

    // Heading Component
    this.componentMap.set('heading', this.transformHeading);
    this.componentMap.set('title', this.transformHeading);

    // Image Component
    this.componentMap.set('image', this.transformImage);

    // Button Component
    this.componentMap.set('button', this.transformButton);

    // Hero Section Component
    this.componentMap.set('hero-section', this.transformHeroSection);
    this.componentMap.set('hero', this.transformHeroSection);

    // Card Component
    this.componentMap.set('card', this.transformCard);

    // Content List Component
    this.componentMap.set('content-list', this.transformContentList);
    this.componentMap.set('list', this.transformContentList);

    // Image Gallery Component
    this.componentMap.set('image-gallery', this.transformImageGallery);
    this.componentMap.set('gallery', this.transformImageGallery);

    // Navigation Menu Component
    this.componentMap.set('navigation-menu', this.transformNavigationMenu);
    this.componentMap.set('navigation', this.transformNavigationMenu);
  }

  /**
   * Transform JSON layout to HTML
   */
  transformLayoutToHTML(layout) {
    if (!Array.isArray(layout)) {
      console.warn('Layout is not an array:', layout);
      return '';
    }

    let html = '';
    
    for (const component of layout) {
      html += this.transformComponent(component);
    }

    return html;
  }

  /**
   * Transform individual component to HTML
   */
  transformComponent(component) {
    const { identifier, type, props = {} } = component;
    const componentType = identifier || type;

    if (!componentType) {
      console.warn('Component missing type:', component);
      return '';
    }

    const transformer = this.componentMap.get(componentType);
    
    if (transformer) {
      return transformer.call(this, props, component);
    } else {
      // Fallback for unknown components
      return this.transformUnknownComponent(props, component);
    }
  }

  /**
   * Transform Text Block Component
   */
  transformTextBlock(props, component) {
    const {
      content = 'Text content goes here...',
      alignment = 'left',
      font_size = 'md',
      max_width = 'none'
    } = props;

    const fontSize = this.getFontSizeValue(font_size);
    const maxWidth = max_width !== 'none' ? max_width : 'none';

    return `
      <div class="text-block" style="text-align: ${alignment}; font-size: ${fontSize}; ${maxWidth !== 'none' ? `max-width: ${maxWidth};` : ''}">
        ${content}
      </div>
    `;
  }

  /**
   * Transform Heading Component
   */
  transformHeading(props, component) {
    const {
      text = 'Heading Component',
      title = text,
      size = 'lg',
      color = '#000000'
    } = props;

    const headingLevel = this.getHeadingLevel(size);
    const fontSize = this.getHeadingSizeValue(size);

    return `
      <h${headingLevel} class="heading" style="color: ${color}; font-size: ${fontSize}; margin: 0.5em 0;">
        ${title || text}
      </h${headingLevel}>
    `;
  }

  /**
   * Transform Image Component
   */
  transformImage(props, component) {
    const {
      src = '',
      alt = 'Image',
      height = '200px',
      width = 'auto'
    } = props;

    if (!src) {
      return `
        <div class="image-placeholder" style="height: ${height}; width: ${width}; background-color: #f7fafc; display: flex; align-items: center; justify-content: center; border: 1px solid #e2e8f0; border-radius: 8px;">
          <span style="color: #718096;">Image Component</span>
        </div>
      `;
    }

    return `
      <img class="image" src="${src}" alt="${alt}" style="height: ${height}; width: ${width}; object-fit: cover; border-radius: 8px;" />
    `;
  }

  /**
   * Transform Button Component
   */
  transformButton(props, component) {
    const {
      text = 'Button',
      label = text,
      colorScheme = 'blue',
      size = 'md',
      variant = 'solid',
      href = '',
      onClick = ''
    } = props;

    const buttonStyles = this.getButtonStyles(colorScheme, size, variant);
    const tag = href ? 'a' : 'button';
    const hrefAttr = href ? `href="${href}"` : '';
    const onClickAttr = onClick ? `onclick="${onClick}"` : '';

    return `
      <${tag} class="button button-${colorScheme} button-${size} button-${variant}" ${hrefAttr} ${onClickAttr} style="${buttonStyles}">
        ${label || text}
      </${tag}>
    `;
  }

  /**
   * Transform Hero Section Component
   */
  transformHeroSection(props, component) {
    const {
      title = 'Hero Section',
      subtitle = 'Hero subtitle goes here',
      background_color = '#f7fafc',
      backgroundColor = background_color,
      text_color = '#000000',
      textColor = text_color,
      cta_text = '',
      cta_link = ''
    } = props;

    const ctaButton = cta_text ? `
      <div style="margin-top: 2rem;">
        <a href="${cta_link}" class="button button-blue button-lg" style="${this.getButtonStyles('blue', 'lg', 'solid')}">
          ${cta_text}
        </a>
      </div>
    ` : '';

    return `
      <section class="hero-section" style="background-color: ${backgroundColor}; color: ${textColor}; padding: 4rem 2rem; text-align: center; border-radius: 8px;">
        <h1 style="font-size: 3rem; font-weight: bold; margin-bottom: 1rem;">${title}</h1>
        <p style="font-size: 1.25rem; color: #718096; margin-bottom: 2rem;">${subtitle}</p>
        ${ctaButton}
      </section>
    `;
  }

  /**
   * Transform Card Component
   */
  transformCard(props, component) {
    const {
      title = 'Card Title',
      content = 'Card content goes here...',
      image = '',
      link = ''
    } = props;

    const imageSection = image ? `
      <img src="${image}" alt="${title}" style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px 8px 0 0;" />
    ` : '';

    const cardContent = `
      <div class="card" style="background: white; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden;">
        ${imageSection}
        <div style="padding: 1.5rem;">
          <h3 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.5rem;">${title}</h3>
          <p style="color: #718096;">${content}</p>
        </div>
      </div>
    `;

    return link ? `<a href="${link}" style="text-decoration: none;">${cardContent}</a>` : cardContent;
  }

  /**
   * Transform Content List Component
   */
  transformContentList(props, component) {
    const {
      limit = 10,
      layout = 'grid',
      columns = 3,
      show_excerpt = true,
      show_date = true,
      show_author = false
    } = props;

    // Generate sample content items
    const items = Array.from({ length: Math.min(limit, 3) }, (_, i) => ({
      title: `Content Item ${i + 1}`,
      excerpt: 'Sample excerpt text...',
      date: `July ${i + 1}, 2025`,
      author: 'Author Name'
    }));

    const itemsHtml = items.map(item => `
      <div class="content-item" style="padding: 1rem; background: white; border: 1px solid #e2e8f0; border-radius: 6px;">
        <h4 style="font-size: 1rem; font-weight: 600; margin-bottom: 0.5rem;">${item.title}</h4>
        ${show_excerpt ? `<p style="font-size: 0.875rem; color: #718096; margin-bottom: 0.5rem;">${item.excerpt}</p>` : ''}
        ${show_date ? `<span style="font-size: 0.75rem; color: #a0aec0;">${item.date}</span>` : ''}
        ${show_author ? `<span style="font-size: 0.75rem; color: #a0aec0; margin-left: 0.5rem;">by ${item.author}</span>` : ''}
      </div>
    `).join('');

    const gridStyles = layout === 'grid' ? `
      display: grid;
      grid-template-columns: repeat(${columns}, 1fr);
      gap: 1rem;
    ` : `
      display: flex;
      flex-direction: column;
      gap: 1rem;
    `;

    return `
      <div class="content-list">
        <h3 style="font-weight: bold; margin-bottom: 1rem;">Content List</h3>
        <div style="${gridStyles}">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Transform Image Gallery Component
   */
  transformImageGallery(props, component) {
    const {
      columns = 3,
      layout = 'grid',
      images = []
    } = props;

    // Generate sample images if none provided
    const galleryImages = images.length > 0 ? images : [
      { src: '', alt: 'Image 1' },
      { src: '', alt: 'Image 2' },
      { src: '', alt: 'Image 3' }
    ];

    const imagesHtml = galleryImages.map((image, i) => {
      if (image.src) {
        return `
          <img src="${image.src}" alt="${image.alt || `Image ${i + 1}`}" 
               style="width: 100%; height: 200px; object-fit: cover; border-radius: 6px;" />
        `;
      } else {
        return `
          <div style="width: 100%; height: 200px; background: #f7fafc; display: flex; align-items: center; justify-content: center; border: 1px solid #e2e8f0; border-radius: 6px;">
            <span style="font-size: 0.875rem; color: #718096;">Image ${i + 1}</span>
          </div>
        `;
      }
    }).join('');

    return `
      <div class="image-gallery">
        <h3 style="font-weight: bold; margin-bottom: 1rem;">Image Gallery</h3>
        <div style="display: grid; grid-template-columns: repeat(${columns}, 1fr); gap: 1rem;">
          ${imagesHtml}
        </div>
        <p style="font-size: 0.75rem; color: #718096; margin-top: 0.5rem;">
          Layout: ${layout} • Columns: ${columns}
        </p>
      </div>
    `;
  }

  /**
   * Transform Navigation Menu Component
   */
  transformNavigationMenu(props, component) {
    const {
      style = 'horizontal',
      items = [
        { label: 'Home', href: '/' },
        { label: 'About', href: '/about' },
        { label: 'Services', href: '/services' },
        { label: 'Contact', href: '/contact' }
      ]
    } = props;

    const navItems = items.map(item => `
      <a href="${item.href}" style="font-size: 0.875rem; color: #4a5568; text-decoration: none; padding: 0.5rem 1rem; border-radius: 4px; transition: color 0.2s;" onmouseover="this.style.color='#3182ce'" onmouseout="this.style.color='#4a5568'">
        ${item.label}
      </a>
    `).join('');

    const navStyles = style === 'horizontal' ? `
      display: flex;
      gap: 1rem;
      align-items: center;
    ` : `
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    `;

    return `
      <nav class="navigation-menu" style="padding: 1rem; background: #f7fafc; border-radius: 6px;">
        <h3 style="font-weight: bold; margin-bottom: 1rem;">Navigation Menu</h3>
        <div style="${navStyles}">
          ${navItems}
        </div>
      </nav>
    `;
  }

  /**
   * Transform Unknown Component
   */
  transformUnknownComponent(props, component) {
    const { name, identifier, type } = component;
    
    return `
      <div class="unknown-component" style="padding: 1rem; background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 6px; text-align: center;">
        <h4 style="color: #718096; font-weight: 600;">${name || identifier || type || 'Unknown'} Component</h4>
        ${identifier && identifier !== type ? `<p style="color: #a0aec0; font-size: 0.875rem;">Type: ${type || identifier}</p>` : ''}
        ${Object.keys(props).length > 0 ? `<p style="color: #a0aec0; font-size: 0.75rem;">${Object.keys(props).length} properties configured</p>` : ''}
      </div>
    `;
  }

  /**
   * Helper: Get font size value
   */
  getFontSizeValue(size) {
    const sizes = {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem'
    };
    return sizes[size] || sizes.md;
  }

  /**
   * Helper: Get heading level
   */
  getHeadingLevel(size) {
    const levels = {
      sm: 6,
      md: 5,
      lg: 4,
      xl: 3,
      '2xl': 2,
      '3xl': 1
    };
    return levels[size] || 4;
  }

  /**
   * Helper: Get heading size value
   */
  getHeadingSizeValue(size) {
    const sizes = {
      sm: '1rem',
      md: '1.25rem',
      lg: '1.5rem',
      xl: '1.875rem',
      '2xl': '2.25rem',
      '3xl': '3rem'
    };
    return sizes[size] || sizes.lg;
  }

  /**
   * Helper: Get button styles
   */
  getButtonStyles(colorScheme, size, variant) {
    const colors = {
      blue: { bg: '#3182ce', text: '#ffffff', border: '#3182ce' },
      green: { bg: '#38a169', text: '#ffffff', border: '#38a169' },
      red: { bg: '#e53e3e', text: '#ffffff', border: '#e53e3e' },
      gray: { bg: '#718096', text: '#ffffff', border: '#718096' }
    };

    const sizes = {
      sm: { padding: '0.5rem 1rem', fontSize: '0.875rem' },
      md: { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      lg: { padding: '1rem 2rem', fontSize: '1.125rem' }
    };

    const color = colors[colorScheme] || colors.blue;
    const sizeStyle = sizes[size] || sizes.md;

    const baseStyles = `
      display: inline-block;
      font-weight: 600;
      text-align: center;
      text-decoration: none;
      border-radius: 6px;
      border: 1px solid;
      cursor: pointer;
      transition: all 0.2s;
      padding: ${sizeStyle.padding};
      font-size: ${sizeStyle.fontSize};
    `;

    if (variant === 'outline') {
      return `${baseStyles}
        background-color: transparent;
        color: ${color.bg};
        border-color: ${color.border};
      `;
    } else {
      return `${baseStyles}
        background-color: ${color.bg};
        color: ${color.text};
        border-color: ${color.border};
      `;
    }
  }

  /**
   * Generate complete HTML page
   */
  generateCompleteHTML(layout, pageData = {}) {
    const {
      title = 'Generated Page',
      meta_description = '',
      meta_title = title
    } = pageData;

    const bodyContent = this.transformLayoutToHTML(layout);

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${meta_title}</title>
    ${meta_description ? `<meta name="description" content="${meta_description}">` : ''}
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            line-height: 1.6;
            color: #2d3748;
            margin: 0;
            padding: 0;
            background-color: #ffffff;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 2rem;
        }
        * {
            box-sizing: border-box;
        }
        img {
            max-width: 100%;
            height: auto;
        }
        .button:hover {
            opacity: 0.9;
            transform: translateY(-1px);
        }
        @media (max-width: 768px) {
            .container {
                padding: 1rem;
            }
            .hero-section h1 {
                font-size: 2rem !important;
            }
            .content-list [style*="grid-template-columns"] {
                grid-template-columns: 1fr !important;
            }
            .image-gallery [style*="grid-template-columns"] {
                grid-template-columns: repeat(2, 1fr) !important;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        ${bodyContent}
    </div>
</body>
</html>`;
  }
}

// Create singleton instance
const componentTransformer = new ComponentTransformer();

export default componentTransformer;